import React, { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export interface StepperElements {
    label: ReactNode;
    indexIcon?: ReactNode;
    details: ReactNode;
    color?: 'primary' | 'primaryDark' | 'neutral';
    variant?: 'outlined' | 'filled';
}

interface Props {
    steps: StepperElements[];
    color?: 'primary' | 'primaryDark' | 'neutral';
    variant?: 'outlined' | 'filled';
    className?: string;
}

export function StepperList({ steps, color = 'primary', variant = 'filled', className = '' }: Props) {
    return (
        <ol className={twMerge('m-0 list-none p-0', className)}>
            {steps.map((step, index) => {
                const stepColor = step.color ?? color;
                const stepVariant = step.variant ?? variant;

                return (
                    <li key={index} className="relative flex gap-4">
                        <div className="relative flex flex-col items-center">
                            <div
                                className={twMerge(
                                    'relative flex size-8 items-center justify-center rounded-full',
                                    stepVariant === 'filled' && stepColor === 'primary' && 'bg-primary-400 text-white',
                                    stepVariant === 'filled' &&
                                        stepColor === 'neutral' &&
                                        'bg-neutral-50 text-neutral-700',
                                    stepVariant === 'filled' &&
                                        stepColor === 'primaryDark' &&
                                        'bg-primary-500 text-white',
                                    stepVariant === 'outlined' &&
                                        stepColor === 'primary' &&
                                        'bg-transparent text-primary-400 ring-1 ring-primary-400',
                                    stepVariant === 'outlined' &&
                                        stepColor === 'neutral' &&
                                        'bg-transparent text-neutral-700 ring-1 ring-neutral-150',
                                    stepVariant === 'outlined' &&
                                        stepColor === 'primaryDark' &&
                                        'bg-transparent text-primary-500 ring-1 ring-primary-500',
                                )}>
                                <span className="text-sm font-medium">
                                    {step.indexIcon ? step.indexIcon : index + 1}
                                </span>
                            </div>

                            {index < steps.length - 1 && (
                                <div
                                    aria-hidden
                                    className={twMerge(
                                        'my-1 w-px flex-1',
                                        stepColor === 'primary' && 'bg-primary-400',
                                        stepColor === 'neutral' && 'bg-neutral-50',
                                        stepColor === 'primaryDark' && 'bg-primary-500',
                                    )}
                                />
                            )}
                        </div>

                        <div
                            className={twMerge(
                                'mt-1 flex-1 text-md font-medium text-neutral-700',
                                index < steps.length - 1 && 'pb-6',
                            )}>
                            <h4 className="mb-1">{step.label}</h4>
                            {step.details}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}
